"use client";
import InteractiveDashboard from "@/app/[locale]/(user-page)/report/[slug]/components/InteractiveDashboard";
import PopupDownload from "@/app/[locale]/(user-page)/report/[slug]/components/PopupDownload";
import PopupDownloadSuccess from "@/app/[locale]/(user-page)/report/[slug]/components/popups/PopupDownloadSuccess";
import PopupPurchase from "@/app/[locale]/(user-page)/report/[slug]/components/popups/PopupPurchase";
import PopupPurchaseSuccess from "@/app/[locale]/(user-page)/report/[slug]/components/popups/PopupPurchaseSuccess";
import PopupSubscribe from "@/app/[locale]/(user-page)/report/[slug]/components/popups/PopupSubscribe";
import ReportContent from "@/app/[locale]/(user-page)/report/[slug]/components/ReportContent";
import WrapperAction from "@/app/[locale]/(user-page)/report/[slug]/components/Wrapper/WrapperAction";
import { useRelatedReports } from "@/hooks/Query/useReport";
import useDeviceDetect from "@/hooks/useMobile";
import {
  setAllowShareReport,
  setPurchaseCompleted,
} from "@/lib/features/reportSlice";
import { useAppDispatch } from "@/lib/hooks";
import { Report, REPORT_TYPE } from "@/models/report";
import { subscription } from "@/models/subscription";
import { User } from "@/models/user";
import ReportServices from "@/services/main/reportServices";
import { Box, Divider } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import {
  Fragment,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import EmbedIframe from "../EmbedIFrame";
import WrapperContainer from "../Wrapper/WrapperContainer";
import ButtonGetReport from "./buttonGetReport";
import ContentImage from "./ContentImage";
import ContentLeft from "./ContentLeft";
import Subscription from "./Subscription";
const RelatedReports = dynamic(
  () =>
    import(
      "@/app/[locale]/(user-page)/report/[slug]/components/RelatedReports"
    ),
  {
    ssr: false,
  }
);

interface DetailsProps {
  slug?: string;
  lang: string;
  isMobile?: boolean;
  type: number;
  mode?: string;
  minSubscription: subscription | null;
}

const Content = (props: DetailsProps) => {
  const { slug, lang, isMobile, type, mode, minSubscription } = props;
  const queryClient = useQueryClient();
  const user = queryClient.getQueryData<User>(["user"]);
  const isLoggedIn = !!user;
  const isMobile2 = useDeviceDetect();
  const dispatch = useAppDispatch();
  const [openPopupDownload, setOpenPopupDownload] = useState(false);
  const [openPopupSubscribe, setOpenPopupSubscribe] = useState(false);
  const [openPopupPurchase, setOpenPopupPurchase] = useState(false);
  const didReplaceRef = useRef<boolean>(false);
  const heightRightRef = useRef<HTMLDivElement | null>(null);
  const pathName = usePathname();
  const { data: dataDetail, isLoading } = useQuery<Report>({
    queryKey: ["report", slug, lang],
    queryFn: async () => {
      const result = await ReportServices.getReportBySlug(slug, lang);
      return result?.data;
    },
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!dataDetail?.slug) return;
    const target = `/${lang}/report/${dataDetail.slug}`;
    if (didReplaceRef.current) return;
    if (pathName === target) {
      didReplaceRef.current = true;
      return;
    }
    window.history.replaceState(null, "", target);
    didReplaceRef.current = true;
  }, [lang, dataDetail?.slug, pathName]);

  useEffect(() => {
    if (dataDetail) {
      const isFree = dataDetail?.type === REPORT_TYPE.FREE;
      const isPaymentEd = dataDetail?.isPaymentEd;
      dispatch(setAllowShareReport(isFree || isPaymentEd));
    }
  }, [dataDetail, dispatch]);
  const handleReportRelatedIds = useMemo(() => {
    const combinedIds = [
      ...(dataDetail?.relatedReportIds || []),
      ...(dataDetail?.relatedDashboardIds || []),
    ].filter((id) => id !== dataDetail?.id);
    const setRelatedReportIds = Array.from(new Set(combinedIds));
    return setRelatedReportIds;
  }, [dataDetail]);
  const { data: dataAllRelatedReport } = useRelatedReports(
    {
      relatedReportIds: handleReportRelatedIds,
    },
    {
      enabled: handleReportRelatedIds?.length > 0,
    }
  );

  const handleOpen = () => {
    switch (dataDetail?.type) {
      case 3: //mua nhiều
        return setOpenPopupSubscribe(true);
      case 2: //mua lẻ
        return setOpenPopupPurchase(true);
      default:
        return setOpenPopupDownload(true);
    }
  };

  const handleOpenPopupDownload = () => {
    setOpenPopupDownload(true);
  };
  const handleClose = () => {
    const temp = dataDetail?.isPaymentEd ? 1 : dataDetail?.type;
    switch (temp) {
      case 3: //mua nhiều
        return setOpenPopupSubscribe(false);
      case 2: //mua lẻ
        return setOpenPopupPurchase(false);
      default:
        return setOpenPopupDownload(false);
    }
  };

  const handleClosePopupPurchaseSuccess = () =>
    dispatch(setPurchaseCompleted(false));

  const handleRenderMode = (mode: string | undefined): ReactNode | null => {
    switch (mode) {
      case "powerbi":
        return (
          <InteractiveDashboard
            minSubscription={minSubscription}
            user={user}
            locale={lang}
            isMobile={isMobile || isMobile2}
            isLoggedIn={isLoggedIn}
            slug={dataDetail?.slug}
            titleReport={dataDetail?.title}
            reportId={dataDetail?.id}
            isLoading={isLoading}
            embedUrl={dataDetail?.embedUrl}
            powerBiId={dataDetail?.powerBiId?.id}
            workspaceId={dataDetail?.workSpaceId?.id}
          />
        );
      case "embed":
        return <EmbedIframe user={user as User} {...dataDetail} />;
      case "pdf":
        if (!dataDetail?.reportAttachments) return null;
        return (
          <ReportContent
            isMobile={isMobile || isMobile2}
            user={user}
            lang={lang}
            reportAttachmentsOption={dataDetail?.reportAttachmentsOption}
            dataDetail={dataDetail}
            isShare={false}
            slug={dataDetail?.slug}
            reportAttachments={dataDetail?.reportAttachments[0]}
            reportId={dataDetail?.id}
            dataDetailType={dataDetail?.type}
            isPaymentEd={dataDetail?.isPaymentEd}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Fragment>
      <WrapperContainer>
        <ContentLeft
          type={type}
          mode={mode}
          report={dataDetail}
          heightRightRef={heightRightRef}
          isLoggedIn={isLoggedIn}
          isMobile={isMobile}
          lang={lang}
        />
        <WrapperAction ref={heightRightRef}>
          <ContentImage
            isLoggedIn={isLoggedIn}
            report={dataDetail}
            slug={slug}
            lang={lang}
            type={type}
          />
          <Box className="flex justify-center">
            <Box className="grid gap-4 sm:gap-6 lg:gap-4 w-full lg:w-full">
              <ButtonGetReport
                mode={mode}
                user={user}
                isLoggedIn={isLoggedIn}
                dataDetail={dataDetail}
                handleOpen={handleOpen}
                setOpenPopupDownload={setOpenPopupDownload}
              />
            </Box>
          </Box>
          <Subscription
            user={user}
            isViewBySubscription={dataDetail?.isViewBySubscription}
          />
        </WrapperAction>
      </WrapperContainer>
      <Divider className="text-gray_10 xl:!w-[1440px] !mx-auto" />
      {handleRenderMode(mode)}
      <Box
        className={!dataDetail?.relatedReports?.length ? "pb-[48px]" : ""}
      ></Box>
      {dataAllRelatedReport && dataAllRelatedReport?.length > 0 && (
        <Box className="flex justify-center">
          <RelatedReports
            queryKey={[
              "report-related",
              {
                relatedReportIds: handleReportRelatedIds,
              },
            ]}
            locale={lang}
            relatedReports={dataAllRelatedReport}
            isMobile={isMobile || isMobile2}
          />
        </Box>
      )}
      <PopupDownload
        isLogin={isLoggedIn}
        dataDetail={dataDetail}
        open={openPopupDownload}
        handleClose={handleClose}
        reportId={dataDetail?.id}
      />
      <PopupDownloadSuccess />
      <PopupSubscribe
        locale={lang}
        user={user}
        isPayment={dataDetail?.isPaymentEd}
        isViewBySubscription={dataDetail?.isViewBySubscription}
        open={openPopupSubscribe}
        handleClose={handleClose}
        isLogin={isLoggedIn}
        dataDetail={dataDetail}
      />
      <PopupPurchase
        open={openPopupPurchase}
        handleClose={handleClose}
        isLogin={isLoggedIn}
        dataDetail={dataDetail}
        locale={lang}
      />
      <PopupPurchaseSuccess
        handleOpenPopupDownload={handleOpenPopupDownload}
        handleClose={handleClosePopupPurchaseSuccess}
      />
    </Fragment>
  );
};

export default Content;
